import React, { Component } from "react";
import { connect } from "react-redux";
import {
    Box,
    TextField,
    Typography,
    CircularProgress,
    IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SelectableChip from "../SelectableChip";
import { getPaginated } from "../../APIHelper";
import { ALERT_SEVERITY } from "../AlertDisplay2";

const ITEMS_PER_PAGE = 20;

class EntitySelectionContent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            listData: [],
            isLoading: false,
            searchParam: "",
        };

        this.page = 0;
        this.completed = false;
        this.isLoading = false;
        this.searchText = null;
        this.listContainerRef = React.createRef();
    }

    componentDidMount() {
        if (this.props.autoLoad !== false) {
            this.resetAndLoad();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.reloadTrigger !== prevProps.reloadTrigger) {
            this.resetAndLoad();
        }
    }

    resetAndLoad() {
        this.page = 0;
        this.completed = false;
        this.isLoading = false;
        this.searchText = null;

        this.setState(
            {
                listData: [],
                isLoading: false,
                searchParam: "",
            },
            () => this.loadData()
        );
    }

    getSelectionData() {
        return this.props.value || [];
    }

    findLocalIndex(item, dataOverride = null) {
        console.log("Finding local index for item: ", item, " in data: ", dataOverride || this.getSelectionData());
        const keyFieldName = this.props.keyFieldName || "id";
        const key = item?.[keyFieldName];
        const data = dataOverride || this.getSelectionData();

        return data.findIndex(
            (x) => String(x?.[keyFieldName]) === String(key)
        );
    }

    isSelected(item) {
        const selectionData = this.getSelectionData();
        const idx = this.findLocalIndex(item, selectionData);

        if (idx === -1) return false;

        const local = selectionData[idx];
        return local?.deselected !== true;
    }

    emitChange(newData) {
        this.props.onChange?.(newData);
    }

    toggleItem(item) {
        const {
            multipleSelection = false,
            deselectionDisabled = false,
            showAlert,
            keyFieldName = "id",
            additionalKeysToAdd = [],
        } = this.props;

        const data = [...this.getSelectionData()];
        const idx = this.findLocalIndex(item, data);

        if (idx === -1) {
            let itemToAdd = {
                [keyFieldName]: item[keyFieldName],
                added: true,
            };

            if (additionalKeysToAdd.length > 0) {
                additionalKeysToAdd.forEach((k) => {
                    itemToAdd[k] = item[k];
                });
            }

            if (multipleSelection) {
                data.push(itemToAdd);
            } else {
                data.splice(0, data.length, itemToAdd);
            }

            this.emitChange(data);
            return;
        }

        const local = { ...data[idx] };
        const currentlySelected = local.deselected !== true;

        if (!currentlySelected) {
            local.deselected = false;
            data[idx] = local;

            if (!multipleSelection) {
                for (let j = 0; j < data.length; j++) {
                    if (j !== idx) {
                        data[j] = { ...data[j], deselected: true };
                    }
                }
            }

            this.emitChange(data);
            return;
        }

        const isNewlyAdded = local.added === true;

        if (deselectionDisabled && !isNewlyAdded) {
            showAlert?.("Bereits hinzugefügte Elemente können im Bearbeitungsmodus nicht entfernt werden.", ALERT_SEVERITY.WARNING);
            return;
        }

        if (isNewlyAdded) {
            data.splice(idx, 1);
        } else {
            data[idx] = { ...local, deselected: true };
        }

        this.emitChange(data);
    }

    async loadData() {
        if (this.isLoading || this.completed) return;

        const activeSearch =
            this.searchText != null && this.searchText.length >= 3;

        this.isLoading = true;
        this.setState({ isLoading: true });

        try {
            console.log("PROPS???: ", this.props);
            console.log("PRELOAD DATA: ", this.props.apiEndpoint, " with page: ", this.page, " ipp: ", ITEMS_PER_PAGE, " search: ", activeSearch ? this.searchText : null);
            const result = await getPaginated({
                endpoint: this.props.apiEndpoint,
                page: this.page,
                ipp: ITEMS_PER_PAGE,
                search: activeSearch ? this.searchText : undefined,
                body: this.props.body,
                method: this.props.method || "GET",
            });

            const data = result?.data || [];
            this.page += 1;

            if (data.length > 0) {
                this.setState((prevState) => ({
                    listData:
                        this.page === 1
                            ? data
                            : [...prevState.listData, ...data],
                }));

                if (data.length < ITEMS_PER_PAGE) {
                    this.completed = true;
                }
            } else {
                this.completed = true;
            }
        } catch (err) {
            console.error("loadData error:", err);
        }

        this.setState({ isLoading: false });
        this.isLoading = false;
    }

    onSearchChange(text) {
        this.setState({ searchParam: text });
        this.searchText = text;

        if (text?.length >= 3) {
            this.page = 0;
            this.completed = false;
            this.isLoading = false;
            this.setState({ listData: [] }, () => this.loadData());
        } else if (text?.length === 0) {
            this.page = 0;
            this.completed = false;
            this.isLoading = false;
            this.searchText = null;
            this.setState({ listData: [] }, () => this.loadData());
        }
    }

    handleScroll = (e) => {
        const target = e.currentTarget;
        const threshold = 120;

        const reachedBottom =
            target.scrollHeight - target.scrollTop - target.clientHeight < threshold;

        if (reachedBottom) {
            this.loadData();
        }
    };

    renderItem = (item) => {
        return (
            <Box
                key={`Entity_${item?.[this.props.keyFieldName || "id"]}`}
                sx={{
                    mb: 1,
                    width: "100%",
                    minWidth: 0,
                }}
            >
                <SelectableChip
                    value={this.isSelected(item)}
                    singleMode={!this.props.multipleSelection}
                    onPress={() => this.toggleItem(item)}
                    style={{
                        width: "100%",
                        minWidth: 0,
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                        }}
                    >
                        {this.props.renderChipContent ? (
                            this.props.renderChipContent(item)
                        ) : (
                            <Typography
                                sx={{
                                    color: "#353535",
                                    fontSize: "0.95rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {String(item?.name ?? item?.title ?? item?.id ?? "")}
                            </Typography>
                        )}
                    </Box>
                </SelectableChip>
            </Box>
        );
    };

    render() {
        const {
            title,
            icon,
            searchPlaceholder,
            hideHeader,
            showCloseButton = false,
            onHeaderClose,
            containerSx,
            listHeight = 400,
        } = this.props;

        return (
            <Box
                sx={{
                    width: "100%",
                    minWidth: 0,
                    backgroundColor: "#fff",
                    borderRadius: 3,
                    p: 0,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    ...containerSx,
                }}
            >
                {!hideHeader && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                            minWidth: 0,
                        }}
                    >
                        {icon ? (
                            <Box
                                component="img"
                                src={typeof icon === "string" ? icon : undefined}
                                alt=""
                                sx={{ width: 22, height: 22, flexShrink: 0 }}
                            />
                        ) : null}

                        <Typography
                            sx={{
                                fontSize: "1.1rem",
                                fontWeight: 700,
                                color: "#222",
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {title || ""}
                        </Typography>

                        {showCloseButton && (
                            <IconButton onClick={() => onHeaderClose?.()}>
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Box>
                )}

                <TextField
                    fullWidth
                    value={this.state.searchParam}
                    placeholder={searchPlaceholder || "Search"}
                    onChange={(e) => this.onSearchChange(e.target.value)}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        sx: {
                            backgroundColor: "#E8E8E8",
                            borderRadius: 2,
                        }
                    }}
                    sx={{ mb: 2 }}
                />

                <Box
                    ref={this.listContainerRef}
                    onScroll={this.handleScroll}
                    sx={{
                        width: "100%",
                        minWidth: 0,
                        height: listHeight,
                        overflowY: "auto",
                        overflowX: "hidden",
                    }}
                >
                    {this.state.listData.length === 0 && !this.state.isLoading ? (
                        <Typography
                            sx={{
                                color: "#676767",
                                textAlign: "center",
                                mt: 2,
                            }}
                        >
                            Keine Daten
                        </Typography>
                    ) : (
                        this.state.listData.map(this.renderItem)
                    )}

                    {this.state.isLoading && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 2,
                            }}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }
}

function mapStateToProps(state) {
    return {};
}

export default connect(mapStateToProps)(EntitySelectionContent);